import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Stack, CircularProgress,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Autocomplete,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import SickOutlinedIcon from '@mui/icons-material/SickOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import { attendanceApi, absenceApi, MyStatus } from '../../services/employeeService';
import { taskApi, TaskListItem, TimerStatus } from '../../services/taskService';
import { useAuthStore } from '../../store/authStore';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PillTone } from '../../components/ui';
import { tokens } from '../../theme';
import { formatDuration, formatTime, formatWeekdayDay, toNumber } from '../../utils/format';

const OPEN_TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'ON_HOLD', 'IN_REVIEW', 'NEEDS_REWORK'];

const STATUS_META: Record<string, { label: string; tone: PillTone; hint: string }> = {
  OFF: { label: 'Poza pracą', tone: 'neutral', hint: 'Zmiana jeszcze się nie rozpoczęła' },
  IN_PROGRESS: { label: 'W pracy', tone: 'success', hint: 'Zmiana trwa' },
  ON_BREAK: { label: 'Wyjście', tone: 'warning', hint: 'Czas wyjścia nie wlicza się do pracy' },
  COMPLETED: { label: 'Zmiana zakończona', tone: 'plum', hint: 'Dzisiejsza zmiana jest zamknięta' },
};

const EVENT_META: Record<string, { label: string; icon: React.ReactNode }> = {
  START: { label: 'Rozpoczęcie pracy', icon: <PlayArrowIcon /> },
  BREAK_OUT: { label: 'Wyjście', icon: <PauseIcon /> },
  BREAK_IN: { label: 'Powrót', icon: <ReplayIcon /> },
  END: { label: 'Zakończenie pracy', icon: <StopIcon /> },
};

const apiStatus = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

/**
 * Pusty stan dla kont bez profilu pracownika (np. manager, ktory nie pracuje z klientami).
 * Wspolny dla stron „Moje sprawy”.
 */
export const NoStaffProfileState: React.FC<{ lead: string }> = ({ lead }) => {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const canAddStaff = role === 'SALON_OWNER' || role === 'SUPER_ADMIN';
  return (
    <SectionCard>
      <EmptyState
        icon={<BadgeOutlinedIcon />}
        title="To konto nie ma profilu pracownika"
        description={(
          <>
            {lead}
            {canAddStaff
              ? ' Jeśli pracujesz z klientami, dodaj siebie na liście Zespół → Pracownicy.'
              : ' Jeśli pracujesz z klientami, poproś właściciela salonu o dodanie Cię do zespołu.'}
          </>
        )}
        actions={canAddStaff ? (
          <Button variant="outlined" onClick={() => navigate('/salon/staff')}>Przejdź do listy pracowników</Button>
        ) : undefined}
      />
    </SectionCard>
  );
};

/** Czas „3 h 42 min” z duzymi cyframi i mniejszymi jednostkami. */
const BigDuration: React.FC<{ minutes: number }> = ({ minutes }) => {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  const unitSx = { fontSize: '1.125rem', fontWeight: 650, color: tokens.inkSoft, ml: 0.5, mr: 1.25, letterSpacing: 0 } as const;
  return (
    <Typography
      component="div"
      aria-label={formatDuration(total)}
      sx={{
        fontSize: { xs: '2.5rem', sm: '2.75rem' },
        fontWeight: 750,
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: tokens.ink,
      }}
    >
      {h > 0 && (
        <>
          {h}
          <Box component="span" sx={unitSx}>h</Box>
        </>
      )}
      {m}
      <Box component="span" sx={{ ...unitSx, mr: 0 }}>min</Box>
    </Typography>
  );
};

export const EmployeeTimePanel: React.FC = () => {
  const [status, setStatus] = useState<MyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [l4Open, setL4Open] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [l4, setL4] = useState({ startDate: '', endDate: '', note: '' });
  const [adjReason, setAdjReason] = useState('');
  const [loadedAt, setLoadedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  // Wybór zadania wykonywanego w trakcie zmiany (timer zadań)
  const user = useAuthStore((s) => s.user);
  const [myTasks, setMyTasks] = useState<TaskListItem[]>([]);
  const [taskChoice, setTaskChoice] = useState<TaskListItem | null>(null);
  const [taskInput, setTaskInput] = useState('');
  const [timer, setTimer] = useState<TimerStatus['session']>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await attendanceApi.myStatus());
      setNoProfile(false);
      setLoadedAt(Date.now());
    } catch (e) {
      if (apiStatus(e) === 404) setNoProfile(true);
      else setToast({ msg: 'Nie udało się pobrać statusu', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadTasks = useCallback(async () => {
    try {
      const res = await taskApi.list({ mine: true, limit: 50 });
      setMyTasks(res.data.filter((t) => OPEN_TASK_STATUSES.includes(t.status) && !t.isArchived));
    } catch { setMyTasks([]); }
    try {
      setTimer((await taskApi.timerStatus()).session);
    } catch { setTimer(null); }
  }, []);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Licznik na ekranie odswiezamy co pol minuty, dopoki zmiana trwa.
  const running = status?.status === 'IN_PROGRESS';
  useEffect(() => {
    if (!running) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, [running]);

  /** Uruchamia timer wybranego zadania; wpisany nowy tytuł najpierw tworzy zadanie. */
  const startTaskTimer = async () => {
    const title = taskInput.trim();
    try {
      let taskId = taskChoice?.id;
      if (!taskId && title.length >= 3) {
        const created = await taskApi.create({
          ...(status?.staff.salonId ? { salonId: status.staff.salonId } : {}),
          title,
          ...(user?.id ? { assigneeIds: [user.id] } : {}),
        });
        taskId = created.id;
      }
      if (!taskId) return;
      setTimer((await taskApi.timerStart(taskId, true)).session);
      setTaskChoice(null);
      setTaskInput('');
      loadTasks();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się uruchomić zadania';
      setToast({ msg, sev: 'error' });
    }
  };

  const startWork = async () => {
    await act(attendanceApi.clockIn, 'Rozpoczęto pracę');
    if (taskChoice || taskInput.trim().length >= 3) await startTaskTimer();
  };

  const endWork = async () => {
    // Zakończenie zmiany domyka też licznik zadania (best-effort)
    if (timer) { try { await taskApi.timerStop(); setTimer(null); } catch { /* ignore */ } }
    await act(attendanceApi.clockOut, 'Zakończono pracę');
  };

  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    if (busy) return; // guard against double-click
    setBusy(true);
    try {
      await fn();
      await load();
      setToast({ msg: okMsg, sev: 'success' });
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Wystąpił błąd';
      setToast({ msg, sev: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const submitL4 = async () => {
    if (!l4.startDate || !l4.endDate) { setToast({ msg: 'Podaj zakres dat', sev: 'error' }); return; }
    await act(() => absenceApi.create({ type: 'SICK_LEAVE', ...l4 }), 'Zgłoszenie L4 wysłane');
    setL4Open(false);
    setL4({ startDate: '', endDate: '', note: '' });
  };

  const submitAdj = async () => {
    if (adjReason.length < 3) { setToast({ msg: 'Podaj powód korekty', sev: 'error' }); return; }
    await act(() => attendanceApi.requestAdjustment(adjReason, status?.openSession?.id), 'Prośba o korektę wysłana');
    setAdjOpen(false);
    setAdjReason('');
  };

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Moje sprawy' }]}
      title="Mój czas pracy"
      description={status
        ? <>Cześć, {status.staff.firstName}! Dziś jest {formatWeekdayDay(new Date())}.</>
        : 'Rozpoczęcie i zakończenie zmiany, wyjścia w trakcie dnia.'}
    />
  );

  const toastEl = (
    <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
    </Snackbar>
  );

  if (loading) {
    return (
      <Box>
        {header}
        <LoadingState variant="block" height={260} />
      </Box>
    );
  }
  if (!status) {
    return (
      <Box>
        {header}
        {noProfile ? (
          <NoStaffProfileState lead="Czas pracy jest ewidencjonowany tylko dla osób dodanych do zespołu salonu." />
        ) : (
          <SectionCard>
            <EmptyState
              icon={<ErrorOutlineIcon />}
              title="Nie udało się wczytać Twojej zmiany"
              description="Sprawdź połączenie z internetem i spróbuj ponownie."
              actions={<Button variant="outlined" onClick={() => { setLoading(true); load(); }}>Spróbuj ponownie</Button>}
            />
          </SectionCard>
        )}
        {toastEl}
      </Box>
    );
  }

  const meta = STATUS_META[status.status] || STATUS_META.OFF;
  // Otwarta zmiana bywa zapisana z data poprzedniego dnia (np. po polnocy UTC) i wtedy
  // nie ma jej w todaySessions — pokazujemy ja mimo to, zeby historia i licznik nie byly puste.
  const open = status.openSession;
  const openMissing = Boolean(open && !status.todaySessions.some((s) => s.id === open.id));
  const sessions = open && openMissing ? [...status.todaySessions, open] : status.todaySessions;
  const allEvents = sessions
    .flatMap((s) => s.events || [])
    .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime());

  // Backend przelicza minuty przy kazdym zdarzeniu, wiec w trakcie zmiany
  // dokladamy czas od ostatniego rozpoczecia/powrotu — tylko do wyswietlenia.
  const lastResume = (open?.events || allEvents)
    .filter((e) => e.type === 'START' || e.type === 'BREAK_IN')
    .map((e) => new Date(e.occurredAt).getTime())
    .sort((a, b) => b - a)[0];
  const sinceResume = running && lastResume ? Math.max(0, Math.floor((Math.max(now, loadedAt) - lastResume) / 60000)) : 0;
  const displayMinutes = toNumber(status.totalActualMinutes) + (openMissing ? toNumber(open?.actualWorkMinutes) : 0) + sinceResume;
  const breaksCount = allEvents.filter((e) => e.type === 'BREAK_OUT').length;
  const offShift = status.status === 'OFF' || status.status === 'COMPLETED';

  return (
    <Box>
      {header}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 7fr) minmax(0, 5fr)' },
          alignItems: 'start',
          maxWidth: 1080,
        }}
      >
        <Stack gap={2} sx={{ minWidth: 0 }}>
          {/* Stan zmiany i glowna akcja — widoczne bez przewijania na telefonie */}
          <Paper sx={{ p: { xs: 2.25, md: 3 } }} aria-live="polite">
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
              <StatusPill label={meta.label} tone={meta.tone} />
              <Typography variant="caption" sx={{ color: tokens.muted }}>{meta.hint}</Typography>
            </Stack>

            <Typography variant="overline" component="div" sx={{ mt: 2.25, lineHeight: 1.4 }}>Czas pracy dziś</Typography>
            <BigDuration minutes={displayMinutes} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 1.5,
                mt: 2.25,
                pt: 2,
                borderTop: `1px solid ${tokens.line}`,
              }}
            >
              {[
                { label: 'Rozpoczęcie', value: status.startedAt ? formatTime(status.startedAt) : '—' },
                { label: 'Rozliczeniowy', value: formatDuration(toNumber(status.totalBillableMinutes) + (openMissing ? toNumber(open?.billableWorkMinutes) : 0)) },
                { label: 'Wyjścia', value: String(breaksCount) },
              ].map((item) => (
                <Box key={item.label} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" component="div" sx={{ color: tokens.muted }}>{item.label}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.0625rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Stack gap={1.25} sx={{ mt: 2.5 }}>
              {offShift && (
                <Button size="large" variant="contained" fullWidth startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  disabled={busy} onClick={startWork}>
                  Rozpocznij pracę
                </Button>
              )}
              {status.status === 'IN_PROGRESS' && (
                <>
                  <Button size="large" variant="contained" fullWidth startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <StopIcon />}
                    disabled={busy} onClick={endWork}>
                    Zakończ pracę
                  </Button>
                  <Button size="large" variant="outlined" fullWidth startIcon={<PauseIcon />}
                    disabled={busy} onClick={() => act(attendanceApi.breakOut, 'Zgłoszono wyjście')}>
                    Zgłoś wyjście
                  </Button>
                </>
              )}
              {status.status === 'ON_BREAK' && (
                <Button size="large" variant="contained" fullWidth startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <ReplayIcon />}
                  disabled={busy} onClick={() => act(attendanceApi.breakIn, 'Powrót do pracy')}>
                  Wracam do pracy
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Zadanie — wybor lub wpisanie zadania do wykonania */}
          {(offShift || status.status === 'IN_PROGRESS') && (
            <SectionCard
              title="Zadanie"
              subtitle={offShift ? 'Opcjonalnie — wystartuje razem z rozpoczęciem pracy' : 'Licznik czasu zadania w trakcie zmiany'}
              icon={<AssignmentOutlinedIcon />}
            >
              {timer && (
                <Alert severity="info" sx={{ mb: 1.5 }} icon={<AssignmentOutlinedIcon fontSize="small" />}>
                  W trakcie: <strong>{timer.task.title}</strong>
                </Alert>
              )}
              <Autocomplete
                freeSolo
                options={myTasks}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.title)}
                value={taskChoice}
                onChange={(_, v) => setTaskChoice(typeof v === 'string' ? null : v)}
                inputValue={taskInput}
                onInputChange={(_, v) => setTaskInput(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Wybierz zadanie lub wpisz nowe"
                    placeholder="np. Inwentaryzacja magazynu"
                    helperText={offShift ? 'Nowo wpisane zadanie zostanie utworzone i przypisane do Ciebie.' : undefined}
                  />
                )}
              />
              {status.status === 'IN_PROGRESS' && (taskChoice || taskInput.trim().length >= 3) && (
                <Button sx={{ mt: 1.5 }} variant="outlined" startIcon={<PlayArrowIcon />} onClick={startTaskTimer}>
                  {timer ? 'Przełącz na to zadanie' : 'Rozpocznij zadanie'}
                </Button>
              )}
            </SectionCard>
          )}

          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ ml: -1.5 }}>
            <Button variant="text" startIcon={<SickOutlinedIcon />} onClick={() => setL4Open(true)}>
              Zgłoś L4
            </Button>
            <Button variant="text" startIcon={<EditCalendarOutlinedIcon />} onClick={() => setAdjOpen(true)}>
              Poproś o korektę
            </Button>
          </Stack>
        </Stack>

        {/* Historia dnia */}
        <SectionCard title="Historia dnia" subtitle={formatWeekdayDay(new Date())} icon={<HistoryIcon />} padding="none">
          {allEvents.length === 0 ? (
            <EmptyState
              compact
              title="Brak wpisów z dzisiaj"
              description="Rozpocznij pracę, aby zarejestrować pierwszy wpis."
            />
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {allEvents.map((e) => {
                const em = EVENT_META[e.type] || { label: e.type, icon: <HistoryIcon /> };
                return (
                  <Stack
                    component="li"
                    key={e.id}
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${tokens.line}`, '&:last-of-type': { borderBottom: 0 } }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                        bgcolor: e.type === 'BREAK_OUT' ? tokens.warningSoft : tokens.plumSoft,
                        color: e.type === 'BREAK_OUT' ? tokens.warningText : tokens.plum,
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                      }}
                    >
                      {em.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{em.label}</Typography>
                      {e.isManual && <StatusPill label="Korekta" tone="warning" size="small" noDot sx={{ mt: 0.25 }} />}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '1rem' }}>
                      {formatTime(e.occurredAt)}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          )}
        </SectionCard>
      </Box>

      {/* L4 dialog */}
      <Dialog open={l4Open} onClose={() => setL4Open(false)} fullWidth maxWidth="sm" aria-labelledby="l4-title">
        <DialogTitle id="l4-title">Zgłoś zwolnienie lekarskie (L4)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: tokens.inkSoft, mb: 2 }}>
            Zgłoszenie trafi do przełożonego. Podaj daty z zaświadczenia.
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <TextField label="Od" type="date" InputLabelProps={{ shrink: true }} value={l4.startDate}
              onChange={(e) => setL4({ ...l4, startDate: e.target.value })} fullWidth sx={{ minWidth: 170 }} />
            <TextField label="Do" type="date" InputLabelProps={{ shrink: true }} value={l4.endDate}
              onChange={(e) => setL4({ ...l4, endDate: e.target.value })} fullWidth sx={{ minWidth: 170 }} />
            <TextField label="Notatka (opcjonalnie)" multiline minRows={2} value={l4.note}
              onChange={(e) => setL4({ ...l4, note: e.target.value })} fullWidth sx={{ gridColumn: '1 / -1' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setL4Open(false)}>Anuluj</Button>
          <Button variant="contained" onClick={submitL4} disabled={busy}>Wyślij zgłoszenie</Button>
        </DialogActions>
      </Dialog>

      {/* Adjustment dialog */}
      <Dialog open={adjOpen} onClose={() => setAdjOpen(false)} fullWidth maxWidth="sm" aria-labelledby="adj-title">
        <DialogTitle id="adj-title">Poproś o korektę czasu pracy</DialogTitle>
        <DialogContent>
          <TextField
            label="Co trzeba poprawić?"
            multiline
            minRows={3}
            value={adjReason}
            fullWidth
            onChange={(e) => setAdjReason(e.target.value)}
            placeholder="np. Zapomniałam zakończyć zmianę — skończyłam o 17:00."
            helperText="Przełożony zobaczy prośbę i poprawi godziny w ewidencji."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setAdjOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={submitAdj} disabled={busy}>Wyślij prośbę</Button>
        </DialogActions>
      </Dialog>

      {toastEl}
    </Box>
  );
};

export default EmployeeTimePanel;
